/**
 * Service to manage creation and editing of SurveyNotes - used by SurveyNoteForm.
 * Most of this logic is for:
 *
 * 1. Facilitating binding to elements of the note structure by components used in
 *   SurveyNoteEdit/SurveyNoteForm (ReportWidgetGoal, ReportWidgetResources, etc.)
 *   to customize the plan of care;
 *
 * 2. Generating parts of the note text automatically, reflecting the selections in the above components.
 *
 * The above components bind to the relevant fields in the draftSurveyNote in the store,
 * and SurveyNoteEdit watches those fields to trigger autosave of the note when they change.
 *
 * Manual saving (either as a draft, or to lock the note) is handled in SurveyNoteEdit.
 */

import * as _ from 'lodash'
import { store } from '@/apps/default/store'
import User from '@/apps/default/User'
import Handouts from '@/providers/Handouts'
import Api from '@/providers/Api'
import i18n from '@/providers/i18n'
import Utils from '@/providers/Utils'
import MetricsUtil from '@/providers/Metrics'
import Attachments from '@/providers/Attachments'
import moment from 'moment'

const GENERATE_SUMMARIES_FOR = {
    s: ['SCORE_EXER', 'SCORE_SED', 'SCORE_DIET', 'SCORE_STRESS', 'SCORE_SLEEP'],
    o: [
        'BIO_BMI', 'SCORE_BP', 'BIO_LDL', 'BIO_HDL',
        'SCORE_GAIT_SPEED', 'SCORE_SIT_TO_STAND', 'SCORE_STAIRS', 'SCORE_SIT_REACH', 'SCORE_CURLS'
    ],
    a: ['TOTAL_SCORE', 'FRAMINGHAM_SCORE', 'ACC_AHA_SCORE'],
    //p: [] these are triggered by setting a goal in ReportPlan, OR by selections/entries in ReportWidgetPlan
}

const SOAP_FIELDS = {
    s: 'subjective',
    o: 'objective',
    a: 'assessment',
    p: 'plan_of_care',
}

const doAutosave = (vue) => {
    rebuildNote()
    store.dispatch('autosaveSurveyNote')
        .catch((e) => {
            Utils.handleError(e, 'Error saving note')
        })
}

const initDraftSurveyNote = (participantId, noteType) => {
    const dsn = {
        participant_id: participantId,
        practitioner_id: User.practitionerId(),
        locking_practitioner_id: null,
        note_type: noteType,

        drafted: null,
        locked: null,
        lastupdated: null,

        chief_complaint: '',
        text: {
            includeGenerated: {},
            generated: { s: '', o: '', a: '', p: '' },
            entered: { s: '', o: '', a: '', p: '' },
        },

        // these store generated + entered text for each letter
        subjective: '',
        objective: '',
        assessment: '',
        plan_of_care: '',

        // flags etc.
        review_minutes: null,
        ccm_review_minutes: null,
        ccm_demographics: false,
        ccm_problem_list: false,
        ccm_symptom_management: false,
        ccm_medication_use: false,
        ccm_allergies: false,
        ccm_clinical_summary: false,
        ccm_goals: false,
        cancer_screening_due: false,
        cancer_screening_types: '',
        isCcm: false, // non-persistent attribute - used to control Provider.chronic_care_start

        plan_goals: initPlanGoals(),
        plan_options: initPlanOptions(),
        handouts: initHandouts(),
        other_suggestions: '',

        // superbill - edited in SuperbillFormModal, SuperbillForm
        superbill: {
            cptCodes: [], // e.g. [{code: '0591T', modifier: '-02', minutes: 10}]
            dxCodes: [], // free text entries
            conditionRelatedTo: {
                employment: false,
                autoAccident: false,
                autoAccidentPlace: '',
                otherAccident: false,
            }
        },
    }

    // TODO: change includeGenerated - default to all on for AWV; off for progress/CCM
    _.each(_.keys(dsn.text.includeGenerated), (key) => {
        dsn.text.includeGenerated[key] = noteType == 'awv' ? true : false
    })

    store.state.draftSurveyNote = dsn
}

// goal values for biometrics - indexed by score name; updated by ReportWidgetGoal
const initPlanGoals = () => {
    const planGoals = {}
    _.each(store.state.patientParticipant.latestScoreDefs, (scoreDef) => {
        const scoreName = scoreDef.score.score_name
        if (scoreDef.plan_goal) {
            const template = i18n.getPlanGoalTemplate(scoreName) // e.g. "Goal for LDL: __ mg/dL"
            planGoals[scoreName] = { template: template, value: null }
        }
    })
    return planGoals
}

// selected values for plan of care options - updated by ReportWidgetResources
const initPlanOptions = () => {
    const planOptions = []
    if (store.state.patientParticipant.plan) {
        _.each(store.state.patientParticipant.plan.planResources, (pr) => {
            if (pr.resource_type == 'plan_of_care_option') {
                planOptions.push({
                    value: pr.resource_label,
                    checked: false,
                    resourceKeys: pr.resourceKeysList
                })
            }
        })
    }
    return planOptions
}

// list of selected handouts - updated by SurveyNoteFormHandouts
const initHandouts = () => {
    const handouts = []

    // When routing from RPM page patientParticipantProvider is undefined
    if (store.state.patientParticipantProvider) {
        // custom documents/attachments for practice
        _.each(store.state.patientParticipantProvider.practice.attachments, (att) => {
            handouts.push({ label: att.name, pdf: att.name, group: att.group, checked: false, attachment: att })
        })
        // default handouts
        _.each(Handouts.defaultHandouts, (h) => {
            handouts.push({ label: h.label, group: Attachments.DEFAULT_HANDOUTS_GROUP, pdf: h.pdf, checked: false })
        })
    }
    return handouts
}

const rebuildNote = () => {
    const dsn = store.state.draftSurveyNote
    if (dsn.text) {
        updateGenerated()

        _.each(['s', 'o', 'a', 'p'], (letter) => {
            const generated = dsn.text.generated[letter]
            const entered = dsn.text.entered[letter]
            let text = ''
            if (entered.length) {
                text += entered + '\n\n'
            }
            if (generated.length) {
                text += generated + '\n'
            }

            if (text.length > 1) {
                dsn[SOAP_FIELDS[letter]] = text
            } else {
                dsn[SOAP_FIELDS[letter]] = ''
            }
        })
    }
}

const updateGenerated = () => {
    const dsn = store.state.draftSurveyNote
    if (dsn.note_type == 'RPM_REVIEW') {
        updateGeneratedRPM()
    } else {
        updateGeneratedSOA()
        updateGeneratedP()
    }
}

const updateGeneratedRPM = () => {
    const dsn = store.state.draftSurveyNote
    dsn.text.generated.o = ''
    _.each(store.state.patientParticipant.unbilled_checks, (check) => {
        let line = `${moment.utc(check.created).local().format('M/D/YY')} - ${MetricsUtil.checkStatusLabels()[check.status]['label']}`
        if (check.comment) {
            line += `- ${check.comment}`
        }
        dsn.text.generated.o += line + '\n'
    })
    // NB: in this case we are not putting anything in S, A or P
    dsn.text.generated.s = ''
    dsn.text.generated.a = ''
    dsn.text.generated.p = ''
}

const updateGeneratedSOA = () => {
    const dsn = store.state.draftSurveyNote
    // add items for S, O, A
    _.each(_.keys(GENERATE_SUMMARIES_FOR), (letter) => {
        const lines = []
        _.each(GENERATE_SUMMARIES_FOR[letter], (scoreName) => {
            if (store.state.patientParticipant.latestScoreDefs) {
                const scoreDef = store.state.patientParticipant.latestScoreDefs[scoreName]
                if (scoreDef) {
                    const ti = scoreDef.threshold_index
                    // e.g. "BMI: __" or "Movement Score" (no colon if no blank)
                    const summary = i18n.getNoteSummaryTemplate(scoreName)

                    if (scoreName == 'FRAMINGHAM_SCORE' || scoreName == 'ACC_AHA_SCORE') {
                        const value = scoreDef.score.score
                        let displayValue = 'N/A'
                        if (value) {
                            displayValue = Number(value * 100).toFixed(0) + '%'
                        }
                        let line = summary.replace('__', displayValue)
                        dsn.text.generated[scoreName] = line
                        if (dsn.text.includeGenerated[scoreName]) {
                            lines.push(line)
                        }
                    } else if (summary && ti !== null) {
                        let line = ''
                        let displayValue = 'N/A'

                        // HACK: special display value for BP
                        if (scoreName == 'SCORE_BP') {
                            // "Blood Pressure: 120/80 mm Hg (Normal)"
                            const sys = store.state.patientParticipant.latestScoreDefs['BIO_BP_SYS'].score.value
                            const dia = store.state.patientParticipant.latestScoreDefs['BIO_BP_DIA'].score.value
                            if (sys && dia) {
                                displayValue = sys + '/' + dia
                            }
                            line = summary.replace('__', displayValue)
                            if (displayValue != 'N/A') {
                                line += ` (${i18n.getNoteSummaryThresholdDescription(scoreName, ti)})`
                            }
                        } else if (summary.indexOf('__') != -1) {
                            // normal numerical biometric, e.g. "BMI: 33"
                            const value = scoreDef.score.value
                            if (value) {
                                displayValue = Number(value).toFixed(1)
                            }
                            // TODO: allow_decimal??
                            line = summary.replace('__', displayValue)
                            if (displayValue != 'N/A') {
                                line += ` (${i18n.getNoteSummaryThresholdDescription(scoreName, ti)})`
                            }
                        } else {
                            // non-numeric, e.g. "Movement Score: Fair"
                            line = `${summary}: ${i18n.getNoteSummaryThresholdDescription(scoreName, ti)}`
                        }

                        dsn.text.generated[scoreName] = line
                        if (dsn.text.includeGenerated[scoreName]) {
                            lines.push(line)
                        }
                    }
                }
            }
        })
        dsn.text.generated[letter] = lines.join('\n')
        //console.log(letter, dsn.text.generated[letter])
    })
}

const updateGeneratedP = () => {
    const dsn = store.state.draftSurveyNote
    const pLines = []

    // add biometric goals
    _.each(dsn.plan_goals, (planGoal) => {
        if (planGoal.value) {
            const line = planGoal.template.replace('__', planGoal.value)
            pLines.push(line)
        }
    })

    // add selected plan of care options
    const checkedOptions = _.filter(dsn.plan_options, (po) => po.checked)
    const checkedOptionValues = _.map(checkedOptions, (po) => po.value)
    if (checkedOptions.length) {
        const lines = 'Resources:\n' + checkedOptionValues.join('\n')
        pLines.push(lines)
    }

    // add wellness habits/goals
    const wellnessPlan = store.state.patientParticipant.wellness_plan
    if (wellnessPlan) {
        if (wellnessPlan.habits.length) {
            const visibleHabits = _.filter(wellnessPlan.habits, (h) => !h.archivedTimestamp)
            const habitList = _.map(visibleHabits, (h) => h.habit)
            const lines = 'Wellness Habits:\n' + habitList.join('\n')
            pLines.push(lines)
        }
        if (wellnessPlan.goals.length) {
            const goals = _.map(wellnessPlan.goals, (g) => g.goal)
            const lines = 'Wellness Goals:\n' + goals.join('\n')
            pLines.push(lines)
        }
    }

    // add other suggestions
    const other = dsn.other_suggestions
    if (other && other.trim()) {
        const line = 'Other Suggestions:\n' + other
        pLines.push(line)
    }

    dsn.text.generated.p = pLines.join('\n\n')
}

// ---------------- exports ----------------

export default {

    NOTE_TYPES: {
        PROGRESS: 'PROGRESS',
        CCM: 'CCM',
        AWV: 'AWV',
        COACHING: 'COACHING',
        RPM_OUTREACH: 'RPM_OUTREACH',
        RPM_REVIEW: 'RPM_REVIEW',
    },
    NON_RPM_NOTE_TYPES: ['PROGRESS', 'CCM', 'AWV', 'COACHING'],

    hasNoBill (note) {
        return !!note.locked && !note.hasSuperbill
    },

    isDraft (note) {
        return !note.locked
    },

    isLocked (note) {
        return !!note.locked
    },

    autosave: _.debounce(doAutosave, 1000),

    rebuildNote: rebuildNote,

    loadOrCreateSurveyNote (vue, surveyNoteId, participantId, noteType) {
        if (surveyNoteId) {
            return Api.getItem(vue, 'survey-note', surveyNoteId)
                .then((item) => {
                    store.state.draftSurveyNote = item
                    this.rebuildNote()
                    return Promise.resolve(item)
                })
        } else {
            initDraftSurveyNote(participantId, noteType)
            return Api.createSurveyNote(this, store.state.draftSurveyNote)
                .then((surveyNote) => {
                    store.state.draftSurveyNote = surveyNote
                    this.rebuildNote()
                    return Promise.resolve(surveyNote)
                })
        }
    }
}
